<template>
  <div>

    <o-autocomplete
      :label="t('titles.location')"
      :model-value.number="locationID === 'new' ? undefined : locationID"
      :items="Location.getOptions()"
      @update:modelValue="(newLocationId: number) => router.push({name: route.name as RouteRecordName, params: {locationID: newLocationId}})"
      class="mb-8"
    />

    <v-tabs class="mb-12">

      <v-tab
        :text="t('titles.general')"
        :to="{name: 'locationDetail', params: {locationID: locationID}}"
        exact
      />

      <v-tab
        :text="t('titles.solutions')"
        :to="!isNew ? {name: 'locationSolutions', params: {locationID: locationID}} : undefined"
        :disabled="isNew"
      />

      <v-tab
        :text="t('titles.products')"
        :to="!isNew ? {name: 'locationProductIndex', params: {locationID: locationID}} : undefined"
        :disabled="isNew"
      />

      <v-tab
        v-if="canReadVlps"
        :text="t('titles.venues')"
        :to="!isNew ? {name: 'locationVenueIndex', params: {locationID: locationID}} : undefined"
        :disabled="isNew"
      />

      <v-tab
        v-if="showUsersTab"
        :text="t('titles.users')"
        :to="!isNew ? {name: 'locationUsers', params: {locationID: locationID}} : undefined"
        :disabled="isNew"
      />

      <v-tab
        :text="t('titles.parcs')"
        :to="!isNew ? {name: 'locationParcs', params: {locationID: locationID}} : undefined"
        :disabled="isNew"
      />

      <v-tab
        v-if="showUriTab"
        :text="t('titles.uri')"
        :to="!isNew ? {name: 'locationUri', params: {locationID: locationID}} : undefined"
        :disabled="isNew"
      />

    </v-tabs>

    <v-alert v-if="canReadInternalNotes && location && location?.internalNotes" color="info" icon="$alert" density="compact" class="mb-6">
      <p>This location has Internal Notes. See the <router-link :to="{name: 'locationDetail', params: {locationID: location.ID}}" class="text-white">General</router-link> tab.</p>
    </v-alert>

    <template v-if="location">
      <!-- :key forces the router view to rerender when the route is changed via the location picker -->
      <router-view :key="route.params.locationID.toString()" :location="location"></router-view>
    </template>

  </div>
</template>

<script setup lang="ts">

import { computed, inject } from 'vue'
import { type RouteRecordName, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Location from '../../../models/Location'
import type { Can } from '../../../main'
import { userUriPermissionChecker } from '../../../utils/useUriPermissionChecker'


const { uriPermittedForLocation } = userUriPermissionChecker()

interface Props {
  locationID: number | 'new'
}

const props = defineProps<Props>()

const $can = inject('$can') as Can
const canReadVlps = $can('vlps.read')

const showUsersTab = $can('users.index')

const showUriTab = computed(() => {
  return props.locationID !== 'new' && uriPermittedForLocation(props.locationID)
})

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const canReadInternalNotes = $can('locationInternalNotes.read')

const location = computed(() => {
  let location
  if (props.locationID === 'new') {
    location = new Location()
  } else {
    location = Location.query().where('ID', props.locationID).with('locationHours').first()
  }
  return location
})

const isNew = computed(() => {
  return !location.value || (location.value && !location.value.ID)
})

</script>
